import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { type ConnectLibraryAnswer } from "@/lib/api/connect-library";
import { cn } from "@/lib/utils";
import { colorsV2 } from "@somethings-ui/core";
import { Shuffle } from "lucide-react";
import { isExpandedState, RowAudioRecorder } from "./row-audio-recorder";
import { useConnectLibraryRow } from "./use-connect-library-row";

interface ConnectRowRecorderProps {
  mentorshipId: string;
  level: 1 | 2 | 3;
  questionText: string;
  questionId: string;
  libraryAnswer: ConnectLibraryAnswer | null;
  isAnotherRowRecording: boolean;
  micDeviceId: string | null;
  onRecordingStateChange: (isRecording: boolean) => void;
  onLocalBlobChange: (hasBlob: boolean) => void;
  onShuffleQuestion: () => void;
  shuffleDisabled: boolean;
}

export function ConnectRowRecorder({
  mentorshipId,
  level,
  questionText,
  questionId,
  libraryAnswer,
  isAnotherRowRecording,
  micDeviceId,
  onRecordingStateChange,
  onLocalBlobChange,
  onShuffleQuestion,
  shuffleDisabled,
}: ConnectRowRecorderProps) {
  const row = useConnectLibraryRow({
    mentorshipId,
    level,
    questionId,
    libraryAnswer,
    micDeviceId,
    onRecordingStateChange,
    onLocalBlobChange,
  });

  const expanded = isExpandedState(row.uiState);
  const levelLabel = `0${level}`;

  return (
    <>
      <div
        data-testid={`connect-row-level-${level}`}
        className={cn(
          "flex flex-col rounded-xl transition-colors",
          expanded ? "px-4 py-3" : "px-1 py-2",
        )}
        style={expanded ? { background: colorsV2.surfaces.z3 } : undefined}
      >
        <div className="flex items-center gap-3">
          <span
            className="font-mono text-xs tabular-nums"
            style={{ color: colorsV2.text.tertiary }}
          >
            {levelLabel}
          </span>
          <p
            className="flex-1 text-sm font-medium leading-snug"
            style={{ color: colorsV2.text.primary }}
          >
            {questionText}
          </p>
          <button
            type="button"
            onClick={onShuffleQuestion}
            disabled={shuffleDisabled || isAnotherRowRecording}
            aria-label={`Shuffle level ${level} question`}
            className="rounded-md p-1.5 hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
            style={{ color: colorsV2.text.secondary }}
          >
            <Shuffle className="h-4 w-4" />
          </button>
          {!expanded && (
            <RowAudioRecorder
              state={row.uiState}
              otherRowRecording={isAnotherRowRecording}
              onStartRecording={row.start}
              onStopRecording={row.stop}
              onPlay={row.play}
              onPause={row.pause}
              onDelete={row.requestDelete}
              onReRecord={row.reRecord}
              onRetryPermission={row.retryPermission}
            />
          )}
        </div>
        {expanded && (
          <RowAudioRecorder
            state={row.uiState}
            otherRowRecording={isAnotherRowRecording}
            onStartRecording={row.start}
            onStopRecording={row.stop}
            onPlay={row.play}
            onPause={row.pause}
            onDelete={row.requestDelete}
            onReRecord={row.reRecord}
            onRetryPermission={row.retryPermission}
          />
        )}
      </div>

      <ConfirmActionDialog
        isOpen={row.isDeletePending}
        onClose={row.cancelDelete}
        onConfirm={row.confirmDelete}
        title="Delete this answer?"
        description="Your teen won't see it. You can re-record any time to replace it."
        confirmText="Delete"
        confirmVariant="destructive"
        loading={row.isDeleteMutationPending}
      />
    </>
  );
}
